import { useCallback, useEffect, useState } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { api } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import {
  getPushTokenStorageKey,
  getUserPushStorageKey,
  isPushSupported,
  isSuccessfulResponse,
} from '../utils/pushNotifications';
import {
  getBrowserMessaging,
  getFirebaseVapidKey,
  isFirebaseMessagingConfigured,
} from '../utils/firebaseMessaging';

const readPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }
  return Notification.permission;
};

export const usePushNotifications = () => {
  const { user } = useAppStore();
  const userStorageKey = getUserPushStorageKey(user);

  const supported = isPushSupported();
  const configured = isFirebaseMessagingConfigured();

  const [permission, setPermission] = useState(readPermission);
  const [isEnabling, setIsEnabling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPermission(readPermission());
  }, [user]);

  const enable = useCallback(async ({ requestIfDefault = true } = {}) => {
    setError(null);

    if (!supported || !configured) {
      setError('Push notifications are not supported in this browser.');
      return { ok: false, reason: 'unsupported' };
    }

    if (!userStorageKey) {
      setError('Sign in before enabling notifications.');
      return { ok: false, reason: 'no-user' };
    }

    setIsEnabling(true);
    try {
      let current = Notification.permission;

      if (current === 'default' && requestIfDefault) {
        current = await Notification.requestPermission();
      }
      setPermission(current);

      if (current !== 'granted') {
        return { ok: false, reason: current };
      }

      const registration = await navigator.serviceWorker.ready;
      const messaging = await getBrowserMessaging();
      if (!messaging) {
        setError('Firebase messaging unavailable.');
        return { ok: false, reason: 'no-messaging' };
      }

      const token = await getToken(messaging, {
        vapidKey: getFirebaseVapidKey(),
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        setError('Could not retrieve push token.');
        return { ok: false, reason: 'no-token' };
      }

      const tokenStorageKey = getPushTokenStorageKey(userStorageKey);
      if (window.localStorage.getItem(tokenStorageKey) === token) {
        return { ok: true, token, reused: true };
      }

      const response = await api.saveNotificationToken({ platform: 'web', token });

      if (!isSuccessfulResponse(response)) {
        setError('Saving token failed on the server.');
        return { ok: false, reason: 'save-failed' };
      }

      window.localStorage.setItem(tokenStorageKey, token);
      return { ok: true, token, reused: false };
    } catch (err) {
      console.error('[FCM] enable error', err);
      setError(err?.message || 'Unexpected error enabling push notifications.');
      return { ok: false, reason: 'error', error: err };
    } finally {
      setIsEnabling(false);
    }
  }, [supported, configured, userStorageKey]);

  const subscribeForeground = useCallback(async (handler) => {
    if (!supported || !configured) return () => {};
    const messaging = await getBrowserMessaging();
    if (!messaging) return () => {};
    return onMessage(messaging, handler);
  }, [supported, configured]);

  return {
    supported,
    configured,
    permission,
    isEnabling,
    error,
    enable,
    subscribeForeground,
  };
};

export default usePushNotifications;
